import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  AutocompleteRenderInputParams,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2, COLOR_TEXT_4, COLOR_TEXT_GREY } from 'src/colors';
import CloseIcon from '@mui/icons-material/Close';
import {
  COLOR_PRIMARY,
  GET_MARKET_DATA_CACHE_URL,
  USER_MARKET_COMPARISON_COUNT,
  USER_REPORT_COMPARISON_COUNT,
} from 'src/const';
import { callApi } from 'src/core/callApi';
import MarketDataGrid from './MarketDataGrid';
import { User } from 'src/interfaces/iuser';
import { getSavedReports } from './helper';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';
import { formatDate } from 'src/utils/functions';

const SearchBar = ({ OnBoardingSearchBox, addMarketAfterSearch }) => {
  const handleOnSearchOptionClick = (_, value) => {
    addMarketAfterSearch({
      label: value.searchName,
      details: value,
    });
  };

  return OnBoardingSearchBox('', true, handleOnSearchOptionClick, 'compare', 'compare');
};

// ===============================
// Main component
// ===============================

const MarketComparePage = ({
  user,
  currency,
  isLoggedIn,
  dummyMarket,
  OnBoardingSearchBox,
  handleOnSampleClick,
  setSubscriptionModalOpen,
  isMobileScreen,
  headerNav,
  handleUrlReDirection,
  source,
  handleReport,
}: {
  user: User;
  currency: string;
  isLoggedIn: boolean;
  dummyMarket;
  OnBoardingSearchBox;
  handleOnSampleClick;
  setSubscriptionModalOpen;
  isMobileScreen: boolean;
  headerNav?: () => JSX.Element;
  handleUrlReDirection?;
  source;
  handleReport?: (
    method: any,
    requestBody: null | any,
    id?: string,
    storeID?: boolean,
    hideLoading?: boolean
  ) => Promise<void>;
  setReportComparePageClose?: () => void;
}) => {
  const [marketComparisons, setMarketComparisons] = useState<{ label: string; details: any }[]>([]);
  const [comparisionDataLoading, setComparisionDataLoading] = useState<boolean>(false);
  const [selectedMarketData, setSelectedMarketData] = useState<any>(null);

  const [savedReports, setSavedReports] = useState<any>([]);
  const [comparisionReports, setComparisionReports] = useState<{ label: string; details: any }[]>(
    []
  );

  const [selectedSavedCompareMarket, setSelectedSavedCompareMarket] = useState<string>('');

  const comparison = source === 'market' ? marketComparisons : comparisionReports;
  const setComparison = source === 'market' ? setMarketComparisons : setComparisionReports;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // if (source === 'report') {
    //   window.history.pushState({}, '', '/airbnb-calculator/reports/compare');
    // }

    if (source === 'report') {
      getSavedReports(setSavedReports);
    }

    // return source === 'report' ? ()  => {
    //   window.history.pushState({}, '', '/airbnb-calculator');
    // }: () => {};
  }, []);

  const handleComparisonChange = (index, value) => {
    const updatedComparisons = [...marketComparisons];
    updatedComparisons[index] = { ...updatedComparisons[index], label: value };
    setMarketComparisons(updatedComparisons);
  };

  const handleRemoveComparison = (index, market) => {
    const updatedComparisons = comparison.filter((_, i) => i !== index);
    setComparison(updatedComparisons);

    setSelectedMarketData(prevSelectedMarketData => {
      if (!prevSelectedMarketData) return prevSelectedMarketData;

      const updatedSelectedMarketData = { ...prevSelectedMarketData };
      delete updatedSelectedMarketData[market.label];

      return updatedSelectedMarketData;
    });
  };

  const addMarketAfterSearch = market => {
    setMarketComparisons(prev => [...prev, market]);
  };

  const ReportSearchBar = () => {
    const [inputVal, setInputVal] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);

    return (
      <Autocomplete
        disablePortal
        options={savedReports || []}
        getOptionLabel={(option: any) =>
          option.address + ` (Created at: ${formatDate(option.created_at)})`
        }
        renderOption={(props, option) => (
          <li {...props}>
            <div>
              <span>{option.address}</span>
              <span style={{ fontSize: '12px', color: COLOR_TEXT_GREY, marginLeft: '4px' }}>
                (Created at: {formatDate(option.created_at)})
              </span>
            </div>
          </li>
        )}
        inputValue={inputVal}
        onInputChange={(_, value) => setInputVal(value)}
        value={selectedOption}
        onChange={(_, value) => {
          if (!value) return;
          setSelectedOption(null);
          setInputVal('');
          setComparisionReports(prev => [
            ...prev,
            {
              label: value.id,
              details: { name: value.address },
            },
          ]);
        }}
        sx={{ width: '100%' }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            size="small"
            label={params.inputProps.value ? '' : 'Add Report...'}
            InputLabelProps={{
              shrink: false,
              style: { paddingLeft: '20px', color: COLOR_PRIMARY },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ color: COLOR_PRIMARY }}>
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: {
                color: COLOR_TEXT_4,
                backgroundColor: '#e5f4f4',
                borderRadius: 4,
                padding: '0px 0px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
              },
            }}
            sx={{
              '& .MuiInputLabel-root.Mui-focused': {
                opacity: '0 !important',
              },
            }}
          />
        )}
      />
    );
  };

  const handleComparison = async (userMarketComparisonCount: number = 0) => {
    setComparisionDataLoading(true);
    const marketCachedData: Record<string, any> = {};

    const getMarketCachedData = async (name: string, city: string, countryCode: string) => {
      const URL = GET_MARKET_DATA_CACHE_URL;      
      city = city ? city : '0';

      return callApi(
        URL,
        {
          queryParams: {
            name,
            city,
            countryCode,
          },
        },
        true
      );
    };

    if (marketComparisons.length === 0) {
      setComparisionDataLoading(false);
      return;
    }

    const marketPromises = marketComparisons.map(async market => {
      const marketDetails = market['details'];
      const marketLabel = market['label'];

      const name = marketDetails['name'];
      const countryCode = marketDetails['countryCode'];
      const city = marketDetails['city'];

      marketCachedData[marketLabel] = await getMarketCachedData(name, city, countryCode);
    });

    await Promise.all(marketPromises);

    setComparisionDataLoading(false);

    setSelectedMarketData(marketCachedData);
    localStorage.setItem(USER_MARKET_COMPARISON_COUNT, String(userMarketComparisonCount + 1));
  };

  const handleReportComparison = async (userReportComparisonCount: number) => {
    setComparisionDataLoading(true);
    const marketCachedData: Record<string, any> = {};

    if (comparison.length === 0) {
      setComparisionDataLoading(false);
      return;
    }

    const getMarketCachedData = async (id: string) => {
      const URL =
        'https://3iugob4wql.execute-api.us-east-2.amazonaws.com/prod/airbnbCalculatorReportCRUD';
      return callApi(URL, { queryParams: { id } }, true);
    };

    const marketPromises = comparisionReports.map(async report => {
      const reportID = report['label'];

      marketCachedData[reportID] = await getMarketCachedData(reportID);
    });

    await Promise.all(marketPromises);
    setSelectedMarketData(marketCachedData);
    setComparisionDataLoading(false);
    localStorage.setItem(USER_REPORT_COMPARISON_COUNT, String(userReportComparisonCount + 1));
  };

  const handleComparisonWrapper = () => {
    let userComparisonCount: number =
      Number(
        localStorage.getItem(
          source === 'market' ? USER_MARKET_COMPARISON_COUNT : USER_REPORT_COMPARISON_COUNT
        )
      ) || 0;
    if (isNaN(userComparisonCount)) {
      userComparisonCount = 0;
    }

    if (!isLoggedIn) {
      handleOnSampleClick();
      return;
    }

    if (user.subscriptionPlan === 'starter' && userComparisonCount > 0) {
      handleOnSampleClick();
      return;
    }

    if (isMobileScreen) {
      // First let the container to be rendered, a very small delay is needed
      setTimeout(() => {
        const dataGridBox = document.getElementById('compare-table-container');
        dataGridBox && dataGridBox.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 20);
    }

    if (source === 'market') {
      handleComparison(userComparisonCount);
    } else if (source === 'report') {
      handleReportComparison(userComparisonCount);
    }
  };

  const loadSavedMarketComparisonOptions = () => {
    const savedMarketComparisons = user.savedMarketComparisons || {};
    return Object.keys(savedMarketComparisons);
  };

  const handleSelectSavedCompareMarket = (e: SelectChangeEvent<string>) => {
    if (e.target.value === '') return;

    setSelectedSavedCompareMarket(e.target.value);
    setSelectedMarketData(null);
    const savedMarketComparisons = user.savedMarketComparisons || {};
    const selectedSavedCompareMarketData = savedMarketComparisons[e.target.value];
    setMarketComparisons(selectedSavedCompareMarketData.markets);
  };

  const selectSavedCompareMarkets = (
    <FormControl fullWidth>
      <Select
        value={selectedSavedCompareMarket}
        onChange={handleSelectSavedCompareMarket}
        displayEmpty
        sx={{
          width: '100%',
          maxWidth: '350px',
          marginLeft: 'auto',
        }}
      >
        <MenuItem value="">
          <em style={{ color: COLOR_TEXT_GREY }}>Select from saved comparisons</em>
        </MenuItem>
        {loadSavedMarketComparisonOptions().map(savedMarketComparisionName => (
          <MenuItem value={savedMarketComparisionName}>{savedMarketComparisionName}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobileScreen ? 'column' : 'row',
          minHeight: '100dvh',
          padding: 2,
          gap: 2,
          backgroundColor: '#f5f5f5',
          mt: '20px',
          ml: 'auto',
          mr: 'auto',
          width: '100%',
          pb: isMobileScreen ? '120px' : 2,
          boxSizing: 'border-box',
        }}
      >
        {isMobileScreen && headerNav && <Box sx={{ marginTop: '10px' }}>{headerNav()}</Box>}
        <Box
          sx={{
            width: isMobileScreen ? '100%' : '30%',
            backgroundColor: 'white',
            padding: 3,
            borderRadius: 2,
            boxShadow: 1,
            boxSizing: 'border-box',
          }}
        >
          <Typography variant="h6" color={COLOR_PRIMARY_2} gutterBottom>
            Compare {source === 'market' ? 'Markets' : 'Reports'}
          </Typography>
          <Typography variant="body2" color="gray" sx={{ mb: '20px' }}>
            Compare up to 5 {source === 'market' ? 'markets' : 'Reports'} to gain actionable
            insights into market performance.
          </Typography>

          {comparison.map((market, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={source === 'market' ? market.label : market.details.name}
                onChange={e => handleComparisonChange(index, e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
              />
              <IconButton onClick={() => handleRemoveComparison(index, market)}>
                <CloseIcon />
              </IconButton>
            </Box>
          ))}

          {comparison.length < 5 ? (
            <Box sx={{ mt: '10px', width: '100%' }}>
              {source === 'market' ? (
                <SearchBar
                  OnBoardingSearchBox={OnBoardingSearchBox}
                  addMarketAfterSearch={addMarketAfterSearch}
                />
              ) : source === 'report' ? (
                <ReportSearchBar />
              ) : null}
            </Box>
          ) : null}

          <Button
            fullWidth
            variant="contained"
            sx={{
              marginTop: 2,
              color: '#fff',
              backgroundColor: COLOR_PRIMARY_1,
              '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
            }}
            disabled={marketComparisons.some(comp => comp.label === '')}
            onClick={handleComparisonWrapper}
          >
            Compare
          </Button>

          {source === 'market' && (
            <>
              <Typography variant="body1" color="gray" textAlign={'center'} sx={{ my: '20px' }}>
                - OR -
              </Typography>

              {selectSavedCompareMarkets}

              <Button
                variant="outlined"
                color="error"
                fullWidth
                disabled={marketComparisons.length === 0}
                onClick={() => {
                  setMarketComparisons([]);
                  setSelectedSavedCompareMarket('');
                  setSelectedMarketData(null);
                }}
                sx={{ mt: '20px' }}
              >
                Clear
              </Button>
            </>
          )}
        </Box>

        <Box
          id="compare-table-container"
          sx={{
            flexGrow: 1,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 1,
            display: 'flex',
            justifyContent: 'center',
            p: '10px',
            width: isMobileScreen ? '100%' : '70%',
          }}
        >
          <Box
            sx={{
              width: '100%',
            }}
          >
            {comparisionDataLoading ? (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  height: '300px',
                  mt: '30px',
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                {selectedMarketData ? (
                  <MarketDataGrid
                    data={selectedMarketData}
                    currency={currency}
                    user={user}
                    handleOnSampleClick={handleOnSampleClick}
                    handleComparisonWrapper={handleComparisonWrapper}
                    comparisons={comparison}
                    isMobileScreen={isMobileScreen}
                    source={source}
                    marketComparisons={marketComparisons}
                    savedMarketComparisonConfig={
                      user.savedMarketComparisons?.[selectedSavedCompareMarket]?.gridColumnConfig
                    }
                  />
                ) : (
                  <Typography variant="body1" color="gray" textAlign={'center'} sx={{ mt: '20px' }}>
                    Add your {source}s and access historical metrics.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MarketComparePage;
